import { type ReactNode, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { type UserType } from "@/domain/core";
import { config } from "@/config/environment";
import { useUserContext } from "@/context/user-context";
import { LoadingCard } from "@/components/ui/loading-card";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredUserTypes?: UserType[];
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  requiredUserTypes = [],
  fallbackPath = "/login"
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const {
    user,
    isLoading: userDetailsLoading,
    isError: userDetailsError
  } = useUserContext();
  const location = useLocation();

  const requiresRole = requiredUserTypes.length > 0;

  // Outer shell: settle auth + profile fetch, then render children (registration/error UI)
  // even when no profile object exists. Role-gated nested routes still need a profile.
  const authSettled = !isLoading && !userDetailsLoading && isAuthenticated;
  const isReady = requiresRole ? authSettled && !!user : authSettled;

  const hasAnyUserType = useCallback(
    (requiredTypes: UserType[]) => {
      if (!user || !user.userType || user.userType === "none") return false;
      if (user.registrationRequired) return false;
      return requiredTypes.includes(user.userType);
    },
    [user]
  );

  if (!isAuthenticated && !isLoading && !userDetailsLoading) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    const loginUrl = `${fallbackPath}?returnUrl=${returnUrl}`;
    return <Navigate to={loginUrl} replace />;
  }

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingCard
          spinnerSize="lg"
          fullScreen={true}
          showThemeToggle={true}
        />
        {import.meta.env.DEV && config.debug.enabled && (
          <div className="absolute bottom-4 left-4 text-xs text-muted-foreground space-y-1">
            <p>Auth loading: {isLoading.toString()}</p>
            <p>User details loading: {userDetailsLoading.toString()}</p>
            <p>Authenticated: {isAuthenticated.toString()}</p>
            <p>Profile error: {userDetailsError.toString()}</p>
          </div>
        )}
      </div>
    );
  }

  if (requiresRole && !hasAnyUserType(requiredUserTypes)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
